// src/components/dashboards/TimeRangeSelector.tsx
import { useKindCopy } from "@/context/DashboardKindContext";
import { useState, useCallback, useEffect } from "react";
import {
  Anchor,
  Box,
  Button,
  Group,
  Popover,
  SegmentedControl,
  Text,
  Tooltip,
  UnstyledButton,
  Switch,
  Checkbox,
} from "@mantine/core";
import { DatePicker, type DatesRangeValue, type DateStringValue } from "@mantine/dates";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import {
  startOfDay,
  endOfDay,
  differenceInCalendarDays,
  format,
  isSameYear,
  parseISO,
} from "date-fns";
import { enGB } from "date-fns/locale";
import { SETTINGS_COPY } from "./composition/settingsCopy";
import { CONTROL_VARS, ctlVar } from "./controlStyle";
import { inheritedFontFamily, roleSize, roleTextStyle } from "@/ui/dashboardFontVars";

// Time-range types and the comparison-window math live in a Mantine-free
// module so the fixture-dump script can import the real functions instead of
// re-implementing them (see src/utils/comparisonWindow.ts). Re-exported here
// because this module has been their import path since the beginning.
export type {
  NamedPreset,
  RollingPreset,
  TimeRangePreset,
  ComparisonMode,
  TimeRange,
} from "@/utils/comparisonWindow";
export {
  PRESET_ALIGNMENT,
  WEEKDAY_ALIGNED_YOY_SHIFT_DAYS,
  previousYearAlignment,
  getComparisonDates,
  getPresetDates,
  getDefaultTimeRange,
} from "@/utils/comparisonWindow";

import type {
  ComparisonMode,
  NamedPreset,
  TimeRangePreset,
  TimeRange,
} from "@/utils/comparisonWindow";
import {
  coveredComparisonWindow,
  formatPeriodDates,
  getComparisonDates,
  getComparisonRequest,
  getPresetDates,
  otherPeriodWindow,
  parseRollingPreset,
  rollingPresetLabel,
  type RollingParts,
  rollingPreset,
} from "@/utils/comparisonWindow";
import { getTargetsSummary } from "@/api/targets";
import RollingPeriodInput from "@/components/composition/RollingPeriodInput";

type PresetOption = {
  // The fixed rows only. A rolling window is `rolling_<n>_<unit>` — a family
  // of infinitely many strings, so it is ONE row with a number beside it
  // (below), never an entry in this list.
  value: NamedPreset;
  label: string;
};

// Quick-select presets shown in the time-range dropdown. Order is
// chronologically-natural (Today → year-level) so users can scan by
// granularity. Week starts Monday (see `weekStartsOn: 1` in
// getPresetDates). All labels in English per the customer-facing UI
// rule (CLAUDE.md "Language").
export const PRESET_OPTIONS: PresetOption[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "last_quarter", label: "Last Quarter" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
];

/** What the "Last N …" row opens on when the current period is not already a
 *  rolling window. Four whole weeks: the same default the report's period
 *  control uses (`ReportPeriodSelect.tsx`), so the two surfaces agree. */
const DEFAULT_ROLLING: RollingParts = { n: 4, unit: "weeks", incl: false };

/** The popover's geometry is fixed (plan 2026-10-11 dashboard period scope,
 *  D1): with `width="auto"` the dropdown narrowed and shortened whenever a
 *  click unmounted the rolling controls, so the bottom-end anchored edge and
 *  the "Compare with" row moved under the pointer. The preset column is the
 *  rolling help line's wrap width; the dropdown is that column plus the
 *  one-month calendar panel. */
const PRESET_COLUMN_WIDTH = 220;
const DROPDOWN_WIDTH = 520;

const COMPARISON_OPTIONS = [
  { value: "none", label: "None" },
  { value: "previous_period", label: "Previous period" },
  { value: "previous_year", label: "Previous year" },
  { value: "custom", label: "Custom" },
  // No "Target": on a dashboard the plan is never the comparison itself, only
  // the "Also compare against the target" switch beside a window — a plan
  // rarely covers every metric on a page (plan 2026-09-15-the-target-is-an-
  // extra-comparison-not-a-mode; ADR-0269).
];

/**
 * Has this account got something to compare against?
 *
 * Shared by every selector on the page; the answer is account-wide and does
 * not change while the popover is open. Only a POSITIVE answer is kept: an
 * account that has targets keeps them for the rest of the session, while a
 * "no" is exactly the answer that changes right after the customer uploads
 * a plan and navigates back here without a reload. Memoising it would show
 * "No targets yet" over targets that exist. A FAILURE resolves to `false`,
 * not to "assume yes": offering a comparison that comes back empty on every
 * tile is worse than not offering it, and the user can still reach the
 * settings page the tooltip names.
 */
let targetsStatePromise: Promise<TargetsState> | null = null;

/**
 * The same answer with its reason: `"none"` when the summary SAID there is no
 * plan, `"unknown"` when it could not be asked. Both gate the option shut; only
 * `"none"` may take a remembered "Also compare against the target" away (plan
 * 2026-09-14-the-plan-leg-on-every-surface, S1) — a failed request must not
 * erase a viewer's stored choice.
 */
export type TargetsState = "available" | "none" | "unknown";

export function loadTargetsState(): Promise<TargetsState> {
  if (!targetsStatePromise) {
    const request: Promise<TargetsState> = getTargetsSummary()
      .then((summary): TargetsState =>
        summary.active_version && summary.line_count > 0 ? "available" : "none",
      )
      .catch((): TargetsState => "unknown")
      .then((state) => {
        if (state !== "available" && targetsStatePromise === request) targetsStatePromise = null;
        return state;
      });
    targetsStatePromise = request;
  }
  return targetsStatePromise;
}

export function loadTargetsAvailable(): Promise<boolean> {
  return loadTargetsState().then((state) => state === "available");
}

/** Test seam: drops the cached answer so the next mount asks again. */
export function resetTargetsAvailableCache(): void {
  targetsStatePromise = null;
}


/**
 * Format a date range for display in comparison labels.
 * Includes year when the range is in a different year than now.
 */
function formatComparisonLabel(start: Date, end: Date): string {
  const now = new Date();
  const sameYear = isSameYear(start, now) && isSameYear(end, now);
  const s = format(start, "d MMM", { locale: enGB });
  const e = sameYear
    ? format(end, "d MMM", { locale: enGB })
    : format(end, "d MMM yyyy", { locale: enGB });
  return `vs ${s} – ${e}`;
}

/**
 * The period's display name — "Last Month", or the date range for a custom one.
 *
 * Exported because the chart tooltips label the current period with it. Same
 * function the button uses, deliberately: two places naming the same period
 * differently is the kind of small inconsistency nobody reports and everyone
 * notices.
 */
export function formatButtonLabel(value: TimeRange): string {
  return periodLabel(value.preset, value.startDate, value.endDate);
}

/** `formatButtonLabel` for a period known only by its preset and dates (the
 *  "(default)" rows that name the dashboard's period). */
export function periodLabel(preset: string, startDate: Date, endDate: Date): string {
  const rolling = parseRollingPreset(preset);
  if (rolling) return rollingPresetLabel(rolling);
  if (preset !== "custom") {
    const opt = PRESET_OPTIONS.find((o) => o.value === preset);
    if (opt) return opt.label;
  }
  return `${format(startDate, "d MMM", { locale: enGB })} – ${format(endDate, "d MMM yyyy", { locale: enGB })}`;
}

/**
 * The parts of the "vs …" subtitle under the period button. Exported so the
 * header of a "Copy as image" (dashboards/capture) names the comparison in
 * exactly the button's words.
 */
export function comparisonSubtitle(
  value: TimeRange,
  dataThrough?: string | null,
): { dates: string; otherSuffix: { text: string; tooltip: string } | null; target: boolean } | null {
  // Compute comparison dates for the button subtitle: the days a total
  // compares, when the page says how far its data reaches. A window the rule
  // cannot clip (`unknown`, `full`) and a period with no data yet (`empty`:
  // there are no days to name, the cards say so themselves) keep the
  // requested window.
  const requestedCompDates = getComparisonDates(value);
  // A malformed day is no day: `coveredComparisonWindow` formats it, and an
  // Invalid Date would throw inside the render.
  const throughDay = dataThrough ? parseISO(dataThrough) : null;
  const covered =
    requestedCompDates && throughDay && !Number.isNaN(throughDay.getTime())
      ? coveredComparisonWindow(
          value.startDate,
          value.endDate,
          requestedCompDates.start,
          requestedCompDates.end,
          throughDay,
        )
      : null;
  const compDates =
    covered?.coverage === "clipped" && covered.start && covered.end
      ? { start: covered.start, end: covered.end }
      : requestedCompDates;
  // Only a leg that is asked for: a deduped other period (C1) is not one.
  // The suffix names the period; its tooltip names the dates (C2).
  const appliedOther = getComparisonRequest(value).other;
  const otherSuffix = appliedOther
    ? {
      text: appliedOther.mode === "previous_year" ? " + last year" : " + previous period",
      tooltip: SETTINGS_COPY.otherPeriod.tooltip(
        SETTINGS_COPY.otherPeriod.names[appliedOther.mode],
        formatPeriodDates(appliedOther.dates.start, appliedOther.dates.end),
      ),
    }
    : null;
  if (!compDates) return null;
  return {
    dates: formatComparisonLabel(compDates.start, compDates.end),
    otherSuffix,
    target: Boolean(value.withTarget),
  };
}

/** `comparisonSubtitle` as one line of plain text, or null without a comparison. */
export function comparisonSubtitleText(value: TimeRange, dataThrough?: string | null): string | null {
  const parts = comparisonSubtitle(value, dataThrough);
  if (!parts) return null;
  return `${parts.dates}${parts.otherSuffix?.text ?? ""}${parts.target ? " + target" : ""}`;
}

type Props = {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  /**
   * The last day the page has data for (`DashboardDTO.data_through_date`).
   * When set, the "vs …" subtitle names the days a comparison TOTAL covers:
   * the counterpart of the days the period has data for (ADR-0310), e.g.
   * "vs 22 Sep – 23 Sep 2025" for This Week on a Thursday with data through
   * Tuesday. Unset (the overview, which has no single day), the subtitle is
   * the requested window. Only the label: the request and its cache key stay
   * the requested window.
   */
  dataThrough?: string | null;
  /** Which dashboards this period applies to (footer line). */
  scope?: PeriodScope;
};

// Mantine v8 DatePicker uses YYYY-MM-DD strings for value and onChange.
// We keep our public TimeRange contract in Date objects and convert
// at the picker boundary only.
type DraftRange = DatesRangeValue<DateStringValue>;

const toDateString = (d: Date): DateStringValue => format(d, "yyyy-MM-dd");

const toDraftRange = (start: Date, end: Date): DraftRange => [
  toDateString(start),
  toDateString(end),
];

/** Which dashboards this picker's period applies to — the footer says it
 *  (plan 2026-10-11 dashboard period scope, A3). Absent: no footer line
 *  (a canvas control, the report editor). */
export type PeriodScope =
  | { kind: "overview" }
  | {
      kind: "dashboard";
      own: boolean;
      /** The dashboard has a stored default period (ADR-0496 D3): without a
       *  pick of the viewer's own it shows that, and "follow" goes back to it. */
      hasDefault?: boolean;
      onFollowAll: () => void;
    };

/** The chosen period's text: the control value text role on a dashboard
 *  control, today's look elsewhere (every variable unset). */
const TRIGGER_VALUE_TEXT = {
  fontFamily: inheritedFontFamily("control_value"),
  fontSize: roleSize("control_value", "var(--mantine-font-size-sm)"),
  ...roleTextStyle("control_value", { weight: 400 }),
};

export default function TimeRangeSelector({ value, onChange, dataThrough, scope }: Props) {
  const { say, periodInheriting, periodFollowAll } = useKindCopy();
  const [opened, setOpened] = useState(false);
  const [draftPreset, setDraftPreset] = useState<TimeRangePreset | null>(null);
  const [draftRange, setDraftRange] = useState<DraftRange>(() =>
    toDraftRange(value.startDate, value.endDate),
  );
  const [draftCompMode, setDraftCompMode] = useState<ComparisonMode>(value.comparisonMode);
  // The plan as a second leg beside the window (W1.8; ADR-0267).
  const [draftWithTarget, setDraftWithTarget] = useState<boolean>(value.withTarget === true);
  // The other shifted period beside the window, for KPI cards only (S5).
  const [draftWithOtherPeriod, setDraftWithOtherPeriod] = useState<boolean>(
    value.withOtherPeriod === true,
  );
  const [draftCompRange, setDraftCompRange] = useState<DraftRange>([
    value.comparisonStartDate ? toDateString(value.comparisonStartDate) : null,
    value.comparisonEndDate ? toDateString(value.comparisonEndDate) : null,
  ]);
  // What the number and unit show while the rolling row is open. Kept beside
  // the draft preset rather than derived from it, so the numbers survive a
  // detour through "Last Month" and back.
  const [rollingSeed, setRollingSeed] = useState<RollingParts>(
    () => parseRollingPreset(value.preset) ?? DEFAULT_ROLLING,
  );

  // `"unknown"` (gate shut) until the summary says otherwise — see
  // loadTargetsState.
  const [targetsState, setTargetsState] = useState<TargetsState>("unknown");
  const targetsAvailable = targetsState === "available";
  useEffect(() => {
    let cancelled = false;
    loadTargetsState().then((state) => {
      if (!cancelled) setTargetsState(state);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // A remembered "Also compare against the target" (S1) passes through the
  // same gate Apply does: when the account has no plan any more, the switch is
  // taken away through `onChange`, so the page neither asks for a leg that is
  // empty on every tile nor keeps storing the choice. Only on a definite
  // "none" — a failed summary request leaves the stored choice alone.
  useEffect(() => {
    if (targetsState !== "none" || value.withTarget !== true) return;
    const { withTarget: _dropped, ...rest } = value;
    onChange(rest);
  }, [targetsState, value, onChange]);

  const comparisonOptions = COMPARISON_OPTIONS;

  const handleOpen = useCallback(() => {
    setDraftRange(toDraftRange(value.startDate, value.endDate));
    setDraftPreset(value.preset);
    const rolling = parseRollingPreset(value.preset);
    if (rolling) setRollingSeed(rolling);
    setDraftCompMode(value.comparisonMode);
    setDraftWithTarget(value.withTarget === true);
    setDraftWithOtherPeriod(value.withOtherPeriod === true);
    setDraftCompRange([
      value.comparisonStartDate ? toDateString(value.comparisonStartDate) : null,
      value.comparisonEndDate ? toDateString(value.comparisonEndDate) : null,
    ]);
    setOpened(true);
  }, [value]);

  const handlePresetClick = (preset: TimeRangePreset) => {
    const { startDate, endDate } = getPresetDates(preset);
    setDraftPreset(preset);
    setDraftRange(toDraftRange(startDate, endDate));
  };

  // The rolling row goes through the SAME derivation as a fixed row: the
  // spelling is the choice, `getPresetDates` turns it into the draft window.
  // `RollingPeriodInput` hands out parts already clamped to the unit's bound,
  // so `rollingPreset` (which throws outside it) never sees a typed 999.
  const applyRolling = (parts: RollingParts) => {
    setRollingSeed(parts);
    handlePresetClick(rollingPreset(parts.n, parts.unit, parts.incl));
  };

  const handleCalendarChange = (val: DraftRange) => {
    setDraftRange(val);
    setDraftPreset("custom");
  };

  const handleApply = () => {
    const [start, end] = draftRange;
    if (start && end) {
      const windowMode = draftCompMode === "previous_period" || draftCompMode === "previous_year" || draftCompMode === "custom";
      const otherMode = draftCompMode === "previous_period" || draftCompMode === "previous_year";
      const range: TimeRange = {
        preset: draftPreset || "custom",
        startDate: startOfDay(parseISO(start)),
        endDate: endOfDay(parseISO(end)),
        comparisonMode: draftCompMode,
        // Only a window can carry the plan beside it; under `none` there is
        // nothing beside.
        ...(windowMode && draftWithTarget && targetsAvailable ? { withTarget: true } : {}),
        // Only a shifted window has an "other" one: custom has no counterpart,
        // under `none` there is no window to shift.
        ...(otherMode && draftWithOtherPeriod ? { withOtherPeriod: true } : {}),
      };
      if (draftCompMode === "custom") {
        const cs = draftCompRange[0];
        const ce = draftCompRange[1];
        if (cs && ce) {
          range.comparisonStartDate = startOfDay(parseISO(cs));
          range.comparisonEndDate = endOfDay(parseISO(ce));
        }
      }
      onChange(range);
      setOpened(false);
    }
  };

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // The draft IS a rolling window (so the row is highlighted and the controls
  // are shown), and the numbers those controls display.
  const rollingDraft = parseRollingPreset(draftPreset);
  const rollingShown = rollingDraft ?? rollingSeed;

  // The other shifted window the KPI option would ask for, on the DRAFT: the
  // option names it and is disabled when it is the primary window again (C1).
  const draftOther = (() => {
    const [start, end] = draftRange;
    if (!start || !end) return null;
    return otherPeriodWindow({
      preset: draftPreset || "custom",
      startDate: startOfDay(parseISO(start)),
      endDate: endOfDay(parseISO(end)),
      comparisonMode: draftCompMode,
    });
  })();
  const draftPeriodName = formatButtonLabel({
    preset: draftPreset || "custom",
    startDate: draftRange[0] ? parseISO(draftRange[0]) : value.startDate,
    endDate: draftRange[1] ? parseISO(draftRange[1]) : value.endDate,
    comparisonMode: draftCompMode,
  });
  const otherSameAsPrimary = draftOther?.sameAsPrimary === true;

  const comparison = comparisonSubtitle(value, dataThrough);
  const compLabel = comparison ? (
    <>
      {comparison.dates}
      {comparison.otherSuffix && (
        <Tooltip label={comparison.otherSuffix.tooltip} withArrow>
          <span data-testid="comparison-other-suffix">{comparison.otherSuffix.text}</span>
        </Tooltip>
      )}
      {comparison.target ? " + target" : ""}
    </>
  ) : null;

  return (
    <Popover
      opened={opened}
      onClose={() => setOpened(false)}
      position="bottom-end"
      shadow="lg"
      radius="md"
      width={DROPDOWN_WIDTH}
      // The fixed geometry (D1) made the panel ~780 px tall: on a 900 px
      // screen Apply fell below the fold. The dropdown now never exceeds the
      // space below its button and scrolls inside instead (browser check).
      middlewares={{ flip: true, shift: true, size: { padding: 8 } }}
    >
      <Popover.Target>
        <UnstyledButton
          onClick={handleOpen}
          // On a dashboard's date range control the control style's
          // unselected state draws this box (plan 2026-10-19-control-block-
          // styling, D3). Its `--ctl-*` variables are set only inside a
          // control cell, so everywhere else (the page header) each value
          // falls back to what it always was.
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            minWidth: 220,
            height: ctlVar(CONTROL_VARS.height, "48px"),
            paddingInline: 14,
            border: `${ctlVar(CONTROL_VARS.border_width, "1px")} solid ${ctlVar(CONTROL_VARS.unselected_border, "var(--mantine-color-gray-4)")}`,
            borderRadius: ctlVar(CONTROL_VARS.radius, "var(--mantine-radius-sm)"),
            fontSize: "var(--mantine-font-size-sm)",
            background: ctlVar(CONTROL_VARS.unselected_bg, "var(--mantine-color-body)"),
            // No fallback: unset, the colour inherits, as the button's own
            // `color: inherit` always did.
            color: `var(${CONTROL_VARS.unselected_text})`,
          }}
        >
          <IconCalendar size={18} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
          <Box style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Text size="sm" lh={1.3} style={TRIGGER_VALUE_TEXT}>{formatButtonLabel(value)}</Text>
            {compLabel && (
              <Text size="xs" c="dimmed" lh={1.2}>{compLabel}</Text>
            )}
          </Box>
          <IconChevronDown size={14} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown p={0} data-testid="time-range-dropdown" style={{ overflowY: "auto" }}>
        <Group gap={0} align="stretch" wrap="nowrap">
          {/* Presets panel */}
          <Box
            py="sm"
            data-testid="time-range-presets"
            style={{
              borderRight: "1px solid var(--mantine-color-gray-3)",
              width: PRESET_COLUMN_WIDTH,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 0,
            }}
          >
            {PRESET_OPTIONS.map((opt) => (
              <UnstyledButton
                key={opt.value}
                onClick={() => handlePresetClick(opt.value)}
                px="md"
                py={8}
                style={{
                  fontSize: "var(--mantine-font-size-sm)",
                  backgroundColor:
                    draftPreset === opt.value
                      ? "var(--mantine-color-blue-0)"
                      : undefined,
                  color:
                    draftPreset === opt.value
                      ? "var(--mantine-color-blue-7)"
                      : undefined,
                  fontWeight: draftPreset === opt.value ? 500 : 400,
                  // One line always: the selected weight made "Last N days /
                  // weeks / months" wrap and pushed Compare with down 21 px
                  // (D1, measured in the browser check).
                  whiteSpace: "nowrap",
                }}
              >
                {opt.label}
              </UnstyledButton>
            ))}

            {/* One more row for the rolling window (W2.3; ADR-0267). It is a
                row and not ten rows because `rolling_<n>_<unit>` is a family:
                clicking it picks a window at once (four whole weeks, or
                whatever the number beside it already says), so Apply never
                sees a half-made choice. */}
            <UnstyledButton
              onClick={() => applyRolling(rollingShown)}
              px="md"
              py={8}
              data-testid="time-range-rolling-row"
              style={{
                fontSize: "var(--mantine-font-size-sm)",
                textAlign: "left",
                backgroundColor: rollingDraft ? "var(--mantine-color-blue-0)" : undefined,
                color: rollingDraft ? "var(--mantine-color-blue-7)" : undefined,
                fontWeight: rollingDraft ? 500 : 400,
                // See the preset rows: the selected weight wrapped this label.
                whiteSpace: "nowrap",
              }}
            >
              Last N days to years
            </UnstyledButton>
            {/* Always rendered, disabled with the seed values while the
                draft is not a rolling window: mounting them on a click
                changed the column's height and so moved every row below
                (D1). */}
            <Box px="md" pt={8} pb={2} data-testid="time-range-rolling-controls">
              {/* The shared input (plan 2026-10-16, 4b): the report period,
                  the date range control's default and a block's own period
                  render the same one. Not portalled: a click in a portalled
                  unit dropdown would be a click outside this popover. Three
                  help lines reserved, so the longer sentence of another unit
                  does not change the column height either. */}
              <RollingPeriodInput
                size="xs"
                value={rollingShown}
                onChange={applyRolling}
                disabled={!rollingDraft}
                withinPortal={false}
                reserveHelpLines={3}
                data-testid="time-range-rolling"
              />
            </Box>
          </Box>

          {/* Calendar panel */}
          <Box p="md" style={{ flex: 1, minWidth: 0 }}>
            <DatePicker
              type="range"
              value={draftRange}
              onChange={handleCalendarChange}
              maxDate={new Date()}
              numberOfColumns={1}
              locale="en-GB"
            />
            <Text size="xs" c="dimmed" mt="xs">
              Timezone: {tz}
            </Text>
          </Box>
        </Group>

        {/* Comparison selector */}
        <Box
          px="md"
          py="sm"
          style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
        >
          <Text size="xs" fw={500} mb={6}>Compare with</Text>
          <SegmentedControl
            size="xs"
            fullWidth
            value={draftCompMode}
            onChange={(v) => setDraftCompMode(v as ComparisonMode)}
            data={comparisonOptions}
          />
          {draftCompMode === "custom" && (
            <Box mt="sm">
              <DatePicker
                type="range"
                value={draftCompRange}
                onChange={setDraftCompRange}
                maxDate={new Date()}
                numberOfColumns={1}
                locale="en-GB"
                size="xs"
              />
            </Box>
          )}
          {/* The other shifted period beside the window (Besluit B: a KPI
              card carries up to three comparisons — previous period, last
              year and the target). KPI cards only; charts and tables never
              draw it. Offered under "Previous period" and "Previous year"
              alone, and not gated on a plan: it needs no target. Plan
              2026-09-14-the-plan-leg-on-every-surface-that-still-draws-one, S5.
              A fixed heading and a label that names the concrete period with
              its dates, so the words never flip meaning with the mode above
              (plan 2026-10-11 dashboard period scope, C2). */}
          {(draftCompMode === "previous_period" || draftCompMode === "previous_year") && (
            <Box mt="sm">
              <Text size="xs" fw={500} mb={6}>{SETTINGS_COPY.otherPeriod.heading}</Text>
              <Checkbox
                size="xs"
                label={(() => {
                  const name = SETTINGS_COPY.otherPeriod.names[
                    draftCompMode === "previous_period" ? "previous_year" : "previous_period"
                  ];
                  return draftOther
                    ? SETTINGS_COPY.otherPeriod.option(
                      name,
                      formatPeriodDates(draftOther.dates.start, draftOther.dates.end),
                    )
                    : name;
                })()}
                // Disabled, not hidden, when the other window is the primary
                // one (Last Year, This Year): the reason says so. The draft
                // keeps the viewer's choice for the next period that has one.
                checked={draftWithOtherPeriod && !otherSameAsPrimary}
                disabled={otherSameAsPrimary}
                description={
                  otherSameAsPrimary
                    ? SETTINGS_COPY.otherPeriod.sameAsPrimary(
                      SETTINGS_COPY.otherPeriod.primaryNames[draftCompMode],
                      draftPeriodName,
                    )
                    : undefined
                }
                onChange={(e) => setDraftWithOtherPeriod(e.currentTarget.checked)}
                data-testid="comparison-with-other-period"
              />
            </Box>
          )}
          {/* The plan as a SECOND leg beside the window (W1.8; ADR-0267):
              every chart and table draws the dashed plan line next to the
              window overlay, every KPI tile a second chip. Only offered
              beside a window and only when the account has a plan
              (`targetsAvailable`). */}
          {targetsAvailable
            && (draftCompMode === "previous_period" || draftCompMode === "previous_year" || draftCompMode === "custom") && (
            <Switch
              mt="sm"
              size="xs"
              label="Also compare against the target"
              checked={draftWithTarget}
              onChange={(e) => setDraftWithTarget(e.currentTarget.checked)}
              data-testid="comparison-with-target"
            />
          )}
        </Box>

        {/* Footer */}
        <Group
          justify={scope ? "space-between" : "flex-end"}
          gap="sm"
          p="md"
          wrap="nowrap"
          style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
        >
          {scope && (
            <Text size="xs" c="dimmed" data-testid="period-scope-note" style={{ minWidth: 0 }}>
              {scope.kind === "overview"
                ? SETTINGS_COPY.periodScope.overview
                : scope.own
                  ? (
                    <>
                      {say(SETTINGS_COPY.periodScope.own)}{" "}
                      <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        data-testid="period-scope-follow-all"
                        onClick={() => {
                          scope.onFollowAll();
                          setOpened(false);
                        }}
                      >
                        {scope.hasDefault
                          ? say(SETTINGS_COPY.periodScope.followDefault)
                          : periodFollowAll}
                      </Anchor>
                    </>
                  )
                  : scope.hasDefault
                    ? say(SETTINGS_COPY.periodScope.defaulted)
                    : periodInheriting}
            </Text>
          )}
          <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Button variant="default" size="sm" onClick={() => setOpened(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={!draftRange[0] || !draftRange[1]}
          >
            Apply
          </Button>
          </Group>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Time grain utilities
// ---------------------------------------------------------------------------

export type TimeGrain = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";

/**
 * Auto-compute a sensible default time grain based on the date range span.
 */
export function computeDefaultTimeGrain(startDate: Date, endDate: Date): TimeGrain {
  const days = differenceInCalendarDays(endDate, startDate) + 1;
  if (days < 62) return "daily";       // < ~2 months (last_month/this_month stay daily)
  if (days < 120) return "weekly";     // 2–4 months
  if (days < 548) return "monthly";    // < 18 months
  if (days < 1095) return "quarterly"; // < 3 years
  return "yearly";
}

/**
 * Pick the preferred grain if available, otherwise fall back to the finest available.
 */
export function resolveEffectiveGrain(preferred: TimeGrain, available: TimeGrain[]): TimeGrain {
  if (available.length === 0 || available.includes(preferred)) return preferred;
  return available[0]; // finest available grain
}
